import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Encounter, QuestionnaireResponse } from "@medplum/fhirtypes";
import { Loading, useMedplum } from "@medplum/react";

import { Button, Group, Stack } from "@mantine/core";

import { format } from "date-fns-tz";

import { TextWrapper } from "@/components/TextWrapper";
import { EncounterNoteDifference } from "@/encounters/components/AmendEncounterDetails/components/EncounterNoteDifference";
import { FilesDiff } from "@/encounters/components/shared/Files";
import { TransferOfCareEncounterLinkId } from "@/encounters/constants/transfer-of-care";
import { useEncounterDetailVersionPair } from "@/encounters/hooks/useEncounterDetailVersionPair";
import { fetchAmendmentHistory } from "@/encounters/services/questionnaire-response";
import NotFound from "@/NotFound";

import ClientInformationDiff from "./components/ClientInformationDiff";
import ReceivingProviderDiff from "./components/ReceivingProviderDiff";
import TransferDetailsDiff from "./components/TransferDetailsDiff";
import TransferOfCareDiffHeader from "./components/TransferOfCareDiffHeader";
import TransferOfCareEncounterDiff from "./components/TransferOfCareEncounterDiff";
import TransferProviderDiff from "./components/TransferProviderDiff";

const TransferOfCareVersionDiff = () => {
  const { encounterId, versionId, clientId } = useParams<{
    encounterId: string;
    versionId: string;
    clientId: string;
  }>();

  const [encounter, setEncounter] = useState<Encounter>();
  const [currentVersion, setCurrentVersion] = useState<QuestionnaireResponse>();
  const [previousVersion, setPreviousVersion] = useState<QuestionnaireResponse>();
  const [amendmentHistory, setAmendmentHistory] = useState<QuestionnaireResponse[]>();
  const [currentVersionIndex, setCurrentVersionIndex] = useState<number>(0);
  const [isFetchingInitialData, setIsFetchingInitialData] = useState(true);
  const navigate = useNavigate();
  const medplum = useMedplum();

  useEffect(() => {
    const getAmendmentHistory = async () => {
      medplum.invalidateSearches("QuestionnaireResponse");
      try {
        if (!versionId || !encounterId) return;

        const encounter = await medplum.searchOne("Encounter", {
          _id: encounterId,
        });
        if (!encounter) return;

        setEncounter(encounter);

        const foundAmendmentHistory = await fetchAmendmentHistory(medplum, encounter);

        if (!foundAmendmentHistory) return;
        const currentHistoryVersionIndex = foundAmendmentHistory.findIndex(
          (questionnaireResponse) => questionnaireResponse.meta?.versionId === versionId,
        );

        if (currentHistoryVersionIndex === -1) return;

        setCurrentVersionIndex(currentHistoryVersionIndex);
        setCurrentVersion(foundAmendmentHistory[currentHistoryVersionIndex]);
        setPreviousVersion(foundAmendmentHistory[currentHistoryVersionIndex + 1]);
        setAmendmentHistory(foundAmendmentHistory);
      } finally {
        setIsFetchingInitialData(false);
      }
    };
    getAmendmentHistory();
  }, [versionId, encounterId, medplum]);

  const {
    currentEncounter,
    previousEncounter,
    isLoading: isEncounterPairLoading,
  } = useEncounterDetailVersionPair(encounterId, amendmentHistory, currentVersionIndex);

  if (isFetchingInitialData || !encounter || isEncounterPairLoading) return <Loading />;

  if ((!currentVersion || !previousVersion) && !isFetchingInitialData) return <NotFound />;

  const lastAmendedBy = currentVersion?.meta?.author?.display;
  const lastAmended = currentVersion?.meta?.lastUpdated;

  return (
    <Stack py="md">
      <TransferOfCareDiffHeader questionnaireResponse={currentVersion!} />
      <TransferOfCareEncounterDiff
        currentVersion={currentVersion}
        previousVersion={previousVersion}
        currentEncounter={currentEncounter}
        previousEncounter={previousEncounter}
      />
      <EncounterNoteDifference
        currentVersion={currentVersion}
        previousVersion={previousVersion}
        questionLinkId={TransferOfCareEncounterLinkId.EncounterNotes}
      />
      <ClientInformationDiff currentVersionIndex={currentVersionIndex} />
      <TransferProviderDiff currentVersionIndex={currentVersionIndex} />
      <ReceivingProviderDiff currentVersionIndex={currentVersionIndex} />
      <TransferDetailsDiff currentVersionIndex={currentVersionIndex} />
      <FilesDiff currentVersionIndex={currentVersionIndex} />
      <Group justify="flex-end">
        <Stack align="flex-end">
          <Button
            variant="outline"
            data-testid="exit-amendment-view-button"
            onClick={() => navigate(`/clients/${clientId}/encounters/${encounterId}`)}
          >
            Exit Amendment View
          </Button>
          {lastAmendedBy && lastAmended && (
            <TextWrapper>
              Last amended by {lastAmendedBy} - {format(lastAmended, "MM/dd/yyyy")}
            </TextWrapper>
          )}
        </Stack>
      </Group>
    </Stack>
  );
};

export default TransferOfCareVersionDiff;
